// Routes — 真實資料版（Route 9, PR #99；location 順序指派 PR #216）。
//
// Drawer 同時管路線本身的屬性（name / site / cadence / duration / status）
// 與這條路線的 location 順序（route_locations 表）。新增模式下會先
// POST /v1/routes 拿到 id，再 PUT /v1/routes/:id/locations；編輯模式
// 兩個都是 PATCH/PUT。

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Plus, RefreshCw, ServerCrash, Building2, Trash2, Route as RouteIcon,
  ArrowUp, ArrowDown, X, MapPin, Map as MapIcon, AlertTriangle, Sparkles,
} from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import PageHeader from "../components/PageHeader.js";
import DataTable, { type Column } from "../components/DataTable.js";
import EmptyState from "../components/EmptyState.js";
import TenantPickerEmptyState from "../components/TenantPickerEmptyState.js";
import Skeleton from "../components/Skeleton.js";
import Drawer from "../components/Drawer.js";
import { Field } from "../components/Field.js";
import {
  listRoutes, createRoute, patchRoute, archiveRoute, friendlyRoutesError,
  listRouteLocations, putRouteLocations,
  type RouteRow, type RouteStatus,
} from "../lib/routesApi.js";
import { listSites, type SiteRow } from "../lib/sitesApi.js";
import { listLocations, type LocationRow } from "../lib/locationsApi.js";
import { useDashboardSession } from "../auth/useDashboardSession.js";
import { useConfirmDialog } from "../components/ConfirmDialog.js";

const STATUS_BADGE: Record<RouteStatus, string> = { active: "badge-success", archived: "badge-default" };
const STATUS_LABEL: Record<RouteStatus, string> = { active: "啟用", archived: "已封存" };

type ListState =
  | { status: "loading" }
  | { status: "ready"; rows: RouteRow[] }
  | { status: "error"; message: string };
type DrawerState =
  | null
  | { mode: "new" }
  | { mode: "edit"; route: RouteRow; autoAddLocationId?: string };

interface RcEdit {
  location_id: string;
  /** Cached label for display so we don't re-fetch on every render. */
  label: string;
  code: string;
  expected_dwell_seconds: number | null;
  /** Active task-assignment count for this (route, location) pair —
   *  fetched once on drawer open. 0 → flag「未派發任務」on this row. New
   *  rows added in the drawer default to 0 (the user hasn't saved yet
   *  and even if they had, no assignment exists). */
  active_assignment_count: number;
}

export default function Routes(): React.ReactElement {
  const session = useDashboardSession();
  const tenantParam = session.super_admin ? session.acting_tenant_id ?? null : null;
  const canWrite = session.scopes.includes("write:site") || session.super_admin === true;
  const [state, setState] = useState<ListState>({ status: "loading" });
  const [editing, setEditing] = useState<DrawerState>(null);
  const [sites, setSites] = useState<SiteRow[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = useCallback(async () => {
    setState({ status: "loading" });
    try {
      const r = await listRoutes({ tenantId: tenantParam });
      setState({ status: "ready", rows: r.items });
    } catch (err) { setState({ status: "error", message: friendlyRoutesError(err) }); }
  }, [tenantParam]);
  const loadSites = useCallback(async () => {
    try { const r = await listSites({ tenantId: tenantParam, status: "active", limit: 200 }); setSites(r.items); }
    catch { setSites([]); }
  }, [tenantParam]);

  useEffect(() => { void load(); void loadSites(); }, [load, loadSites]);

  // Deep-link: `/dashboard/routes?edit=<route_id>[&add_location=<cp_id>]`
  // auto-opens the edit drawer for that route once the list finishes
  // loading. Optional `add_location` hint pre-fills the route_locations
  // editor with that location already in the list — the task-assignment
  // 「快速設定」CTA passes both params so the user lands in a drawer where
  // the target is already added and they only need to press 儲存.
  //
  // We always clear the URL params after consuming so a refresh / back
  // doesn't re-trigger the drawer or re-add the location. Unknown ids
  // (deleted / wrong tenant / stale link) → silently clear and stay on
  // the list.
  useEffect(() => {
    if (state.status !== "ready") return;
    const editId = searchParams.get("edit");
    if (!editId) return;
    const addLocationId = searchParams.get("add_location") ?? undefined;
    const target = state.rows.find((r) => r.id === editId);
    if (target) {
      setEditing({ mode: "edit", route: target, autoAddLocationId: addLocationId });
    }
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("edit");
      next.delete("add_location");
      return next;
    }, { replace: true });
  }, [state, searchParams, setSearchParams]);

  const columns: Column<RouteRow>[] = useMemo(() => [
    {
      key: "name", header: "路線名稱", primary: true, width: "30%",
      render: (r) => (
        <div className="flex items-center gap-2">
          <RouteIcon className="h-4 w-4 text-text-muted flex-shrink-0" />
          <span className="font-medium text-text-primary">{r.name}</span>
        </div>
      ),
    },
    {
      key: "site", header: "案場",
      render: (r) => <span className="text-text-secondary">{r.site_name ?? r.site_id}</span>,
    },
    {
      key: "locations", header: "地點",
      render: (r) => (
        <div className="inline-flex items-center gap-1.5">
          <span className="font-mono tabular-nums text-text-secondary">{r.location_count}</span>
          {r.assignment_gap_count > 0 && (
            <span
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-2xs font-medium bg-warning/20 text-warning"
              title="此路線下有地點尚未派發任務"
            >
              <AlertTriangle className="h-3 w-3" />
              缺 {r.assignment_gap_count} 點任務
            </span>
          )}
        </div>
      ),
    },
    {
      key: "cadence", header: "頻率", hideOnMobile: true,
      render: (r) => <span className="font-mono text-text-secondary">{r.cadence_minutes ? `每 ${r.cadence_minutes} 分鐘` : "—"}</span>,
    },
    {
      key: "duration", header: "預估", hideOnMobile: true,
      render: (r) => <span className="font-mono text-text-secondary">{r.expected_duration_seconds ? `${Math.round(r.expected_duration_seconds / 60)} 分鐘` : "—"}</span>,
    },
    { key: "status", header: "狀態", render: (r) => <span className={STATUS_BADGE[r.status]}>{STATUS_LABEL[r.status]}</span> },
    {
      key: "preview", header: "", width: "1%",
      render: (r) => (
        <Link
          to={`/dashboard/routes/${r.id}/preview`}
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-1 rounded border border-border-subtle bg-bg-raised px-2 py-1 text-2xs text-accent hover:text-accent-hover hover:bg-bg-hover"
          title="在平面圖上預覽路線"
        >
          <MapIcon className="h-3 w-3" /> 預覽
        </Link>
      ),
    },
  ], []);

  if (session.super_admin && !tenantParam) {
    return (<><PageHeader title="路線" description="巡邏路線與地點順序" />
      <TenantPickerEmptyState /></>);
  }

  return (
    <>
      <PageHeader
        title="路線"
        description="案場內的巡邏路線。新增 / 編輯時可同時設定路線經過的地點順序。"
        actions={
          <>
            <button className="btn-secondary text-sm" onClick={() => void load()} disabled={state.status === "loading"}>
              <RefreshCw className={`h-4 w-4 ${state.status === "loading" ? "animate-spin" : ""}`} /> 重新整理
            </button>
            {canWrite && <button data-tour-id="routes.new" onClick={() => setEditing({ mode: "new" })} className="btn-primary text-sm" disabled={sites.length === 0}>
              <Plus className="h-4 w-4" /> 新增路線
            </button>}
          </>
        }
      />
      {state.status === "loading" && <ListSkeleton />}
      {state.status === "error" && <EmptyState icon={ServerCrash} title="路線列表載入失敗" description={state.message}
        action={<button className="btn-primary text-sm" onClick={() => void load()}>再試一次</button>} />}
      {state.status === "ready" && state.rows.length === 0 && (
        <EmptyState icon={RouteIcon} title="這個單位還沒有路線"
          description={sites.length === 0 ? "請先到「案場」頁建立至少一個案場。" : "建立第一條路線後，就可以指派地點與班表。"}
          action={canWrite && sites.length > 0 ? <button data-tour-id="routes.new" className="btn-primary text-sm" onClick={() => setEditing({ mode: "new" })}>新增路線</button> : undefined}
        />
      )}
      {state.status === "ready" && state.rows.length > 0 && (
        <DataTable
          rows={state.rows} columns={columns} rowKey={(r) => r.id}
          searchableText={(r) => `${r.name} ${r.site_name ?? ""}`}
          onRowClick={(r) => setEditing({ mode: "edit", route: r })}
        />
      )}
      <Drawer
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing?.mode === "edit" ? `編輯 ${editing.route.name}` : "新增路線"}
        description="路線屬性與地點順序"
      >
        {editing && (
          <RouteForm
            mode={editing.mode}
            route={editing.mode === "edit" ? editing.route : undefined}
            autoAddLocationId={editing.mode === "edit" ? editing.autoAddLocationId : undefined}
            sites={sites} tenantId={tenantParam}
            onClose={() => setEditing(null)}
            onSaved={() => { setEditing(null); void load(); }}
            canArchive={canWrite}
          />
        )}
      </Drawer>
    </>
  );
}

function RouteForm({ mode, route, autoAddLocationId, sites, tenantId, onClose, onSaved, canArchive }: {
  mode: "new" | "edit"; route?: RouteRow; autoAddLocationId?: string;
  sites: SiteRow[]; tenantId: string | null;
  onClose: () => void; onSaved: () => void; canArchive: boolean;
}): React.ReactElement {
  const [name, setName] = useState(route?.name ?? "");
  const initialSiteId = route?.site_id ?? sites[0]?.id ?? "";
  const [siteId, setSiteId] = useState(initialSiteId);
  const [cadence, setCadence] = useState(String(route?.cadence_minutes ?? ""));
  const [duration, setDuration] = useState(String(route?.expected_duration_seconds ? Math.round(route.expected_duration_seconds / 60) : ""));
  const [status, setStatus] = useState<RouteStatus>(route?.status ?? "active");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [siteLocations, setSiteLocations] = useState<LocationRow[]>([]);
  const [rcs, setRcs] = useState<RcEdit[]>([]);
  const [rcLoaded, setRcLoaded] = useState(mode === "new");
  const [pickToAdd, setPickToAdd] = useState<string>("");

  // ── Load existing route_locations when editing ─────────────────────
  useEffect(() => {
    if (mode !== "edit" || !route) return;
    let cancelled = false;
    void (async () => {
      try {
        const r = await listRouteLocations(route.id, tenantId);
        if (!cancelled) {
          setRcs(r.items.map((it) => ({
            location_id: it.location_id,
            label: it.label,
            code: it.code,
            expected_dwell_seconds: it.expected_dwell_seconds,
            active_assignment_count: it.active_assignment_count ?? 0,
          })));
          setRcLoaded(true);
        }
      } catch (e) {
        if (!cancelled) setErr(friendlyRoutesError(e));
      }
    })();
    return () => { cancelled = true; };
  }, [mode, route, tenantId]);

  // ── Load this site's locations (the dropdown options) ──────────────
  useEffect(() => {
    if (!siteId) { setSiteLocations([]); return; }
    let cancelled = false;
    void (async () => {
      try {
        const r = await listLocations({ tenantId, siteId, status: "active", limit: 500 });
        if (!cancelled) setSiteLocations(r.items);
      } catch {
        if (!cancelled) setSiteLocations([]);
      }
    })();
    return () => { cancelled = true; };
  }, [siteId, tenantId]);

  // If site changes from the originally-loaded site, drop any rc whose
  // location isn't in the new site (those would be rejected by the
  // backend's tenant+site validation).
  useEffect(() => {
    if (rcs.length === 0 || siteLocations.length === 0) return;
    const valid = new Set(siteLocations.map((c) => c.id));
    const next = rcs.filter((r) => valid.has(r.location_id));
    if (next.length !== rcs.length) setRcs(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteLocations]);

  const availableForAdd = useMemo(() => {
    const used = new Set(rcs.map((r) => r.location_id));
    return siteLocations.filter((c) => !used.has(c.id));
  }, [siteLocations, rcs]);

  // Auto-add prefill: when arrived via `?add_location=<id>` deep-link
  // (e.g., from the task-assignment「快速設定」CTA), drop that location
  // straight into `rcs` once data has loaded. Saves the operator from
  // hunting the dropdown at the bottom of the drawer. Uses a notice
  // banner so they see exactly what changed and can press 儲存 (or
  // remove the row if it wasn't what they wanted).
  //
  // Why a separate state for `autoAddedId` rather than diffing rcs:
  // we need to remember which add was "auto" so the banner persists
  // until save (or until the user removes that row). Once consumed
  // (rcs contains the id) we keep the banner; if user removes the row
  // manually we clear the banner.
  const [autoAddedId, setAutoAddedId] = useState<string | null>(null);
  const [autoAddedLabel, setAutoAddedLabel] = useState<string | null>(null);
  const [autoAddConsumed, setAutoAddConsumed] = useState(false);
  useEffect(() => {
    if (mode !== "edit" || !autoAddLocationId) return;
    if (autoAddConsumed) return;
    // Wait for both loads to settle before deciding (otherwise we'd see
    // an empty rcs/siteLocations during the initial render and
    // think the location is "available to add" when it's actually
    // already in the route).
    if (!rcLoaded || siteLocations.length === 0) return;
    setAutoAddConsumed(true);
    const cp = siteLocations.find((c) => c.id === autoAddLocationId);
    if (!cp) return; // Hint stale (deleted / different site); silently no-op
    const alreadyIn = rcs.some((r) => r.location_id === autoAddLocationId);
    if (alreadyIn) {
      // Was already in the route — surface the banner anyway so the
      // user understands why they were sent here. Most likely they hit
      // the CTA after someone else added it, or the form data is stale.
      setAutoAddedId(cp.id);
      setAutoAddedLabel(cp.label);
      return;
    }
    setRcs((prev) => [...prev, { location_id: cp.id, label: cp.label, code: cp.code, expected_dwell_seconds: null, active_assignment_count: 0 }]);
    setAutoAddedId(cp.id);
    setAutoAddedLabel(cp.label);
  }, [mode, autoAddLocationId, autoAddConsumed, rcLoaded, siteLocations, rcs]);
  // Clear banner if the operator removes the auto-added row.
  useEffect(() => {
    if (!autoAddedId) return;
    if (!rcs.some((r) => r.location_id === autoAddedId)) {
      setAutoAddedId(null);
      setAutoAddedLabel(null);
    }
  }, [rcs, autoAddedId]);

  const addRc = (): void => {
    if (!pickToAdd) return;
    const cp = siteLocations.find((c) => c.id === pickToAdd);
    if (!cp) return;
    setRcs([...rcs, { location_id: cp.id, label: cp.label, code: cp.code, expected_dwell_seconds: null, active_assignment_count: 0 }]);
    setPickToAdd("");
  };
  const moveRc = (idx: number, dir: -1 | 1): void => {
    const next = [...rcs];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setRcs(next);
  };
  const removeRc = (idx: number): void => {
    setRcs(rcs.filter((_, i) => i !== idx));
  };

  const save = async (): Promise<void> => {
    setBusy(true); setErr(null);
    try {
      const body = {
        name: name.trim(),
        site_id: siteId,
        cadence_minutes: cadence ? Math.max(1, Math.min(1440, Number(cadence))) : null,
        expected_duration_seconds: duration ? Math.max(60, Math.min(86400, Math.round(Number(duration) * 60))) : null,
        status,
      };
      let routeId: string;
      if (mode === "new") {
        const r = await createRoute(body, tenantId);
        routeId = r.id;
      } else if (route) {
        await patchRoute(route.id, body, tenantId);
        routeId = route.id;
      } else {
        return;
      }
      await putRouteLocations(
        routeId,
        rcs.map((r) => ({
          location_id: r.location_id,
          expected_dwell_seconds: r.expected_dwell_seconds,
        })),
        tenantId,
      );
      onSaved();
    } catch (e) { setErr(friendlyRoutesError(e)); } finally { setBusy(false); }
  };
  const dlg = useConfirmDialog();
  const archive = async (): Promise<void> => {
    if (!route) return;
    if (!(await dlg.confirm(`確定要封存路線「${route.name}」？歷史巡邏紀錄保留。`, { destructive: true, okLabel: "封存" }))) return;
    setBusy(true); setErr(null);
    try { await archiveRoute(route.id, tenantId); onSaved(); }
    catch (e) { setErr(friendlyRoutesError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      {autoAddedId && autoAddedLabel && (
        <div className="rounded-lg border border-accent/40 bg-accent/10 p-3 flex items-start gap-2 text-2xs leading-relaxed text-text-primary">
          <Sparkles className="h-3.5 w-3.5 mt-px flex-shrink-0 text-accent" />
          <div className="flex-1">
            <p>
              已從任務派發頁帶入。「<span className="font-medium">{autoAddedLabel}</span>」已加到下方地點列表，按下方
              <span className="mx-1 font-medium text-accent">儲存</span>
              即可完成設定。
            </p>
            <p className="mt-1 text-text-muted">不需要的話，直接從列表移除該點即可。</p>
          </div>
        </div>
      )}
      <Field label="路線名稱" required><input data-tour-id="routes.field.name" className="input" value={name} onChange={(e) => setName(e.target.value)} /></Field>
      <Field label="所屬案場" required hint={mode === "edit" && siteId !== initialSiteId ? "更換案場會清除原本的地點順序" : undefined}>
        <select className="select" value={siteId} onChange={(e) => setSiteId(e.target.value)}>
          {sites.length === 0 && <option value="">— 尚無案場 —</option>}
          {sites.map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
        </select>
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="頻率（分鐘）" hint="每隔多久應跑一次此路線">
          <input className="input font-mono" type="number" min="1" max="1440" value={cadence} onChange={(e) => setCadence(e.target.value)} placeholder="例如：120" />
        </Field>
        <Field label="預估時間（分鐘）" hint="完整跑一次的預估時間">
          <input className="input font-mono" type="number" min="1" max="1440" value={duration} onChange={(e) => setDuration(e.target.value)} placeholder="例如：45" />
        </Field>
      </div>
      <Field label="狀態">
        <select className="select" value={status} onChange={(e) => setStatus(e.target.value as RouteStatus)}>
          <option value="active">啟用</option>
          <option value="archived">封存</option>
        </select>
      </Field>

      {/* ── Location 順序 ─────────────────────────────────────────── */}
      <div className="border-t border-border pt-4 space-y-2">
        <div className="flex items-baseline justify-between">
          <h3 className="text-sm font-medium text-text-primary">地點順序</h3>
          <span className="text-2xs text-text-muted">
            {rcs.length === 0 ? "尚未加入" :
             rcs.length === 1 ? "單點路線（無順序）" :
             `${rcs.length} 點 · 由上到下為巡邏順序`}
          </span>
        </div>

        {rcLoaded && (() => {
          // 醒目提醒：route_locations 有加 location，但對應的
          // location_task_assignments (route × cp) 是 0。也就是「點被
          // 加進路線但沒派發任務」——掃描時不會出現這條路線。
          const gapCount = rcs.filter((r) => r.active_assignment_count === 0).length;
          if (gapCount === 0) return null;
          return (
            <div className="rounded-lg border border-warning/40 bg-warning/10 p-3 flex items-start gap-2 text-warning">
              <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <div className="text-xs leading-relaxed flex-1 text-text-primary">
                <p className="font-medium text-warning">
                  此路線下有 {gapCount} 個地點尚未派發任務
                </p>
                <p className="mt-1 text-text-secondary">
                  巡邏員在這些點感應標籤時，掃描頁不會把此路線列為「進行中」。請到
                  <Link to={mode === "edit" && route ? `/dashboard/tasks` : "/dashboard/tasks"}
                    className="mx-1 underline text-accent hover:text-accent-hover">任務</Link>
                  頁面為該地點在此路線下建立任務派發（含 RRULE 時段）。
                </p>
              </div>
            </div>
          );
        })()}

        {!rcLoaded ? (
          <div className="text-xs text-text-muted py-3">讀取中…</div>
        ) : rcs.length === 0 ? (
          <div className="text-xs text-text-muted text-center py-3 rounded border border-dashed border-border-subtle">
            尚未指派地點。請從下方下拉選單加入。
          </div>
        ) : (
          <ul className="space-y-1.5">
            {rcs.map((rc, idx) => (
              <li
                key={rc.location_id}
                className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border ${
                  rc.active_assignment_count === 0
                    ? "bg-warning/5 border-warning/40"
                    : "bg-bg-raised border-border-subtle"
                }`}
              >
                <span className="text-xs font-mono text-text-muted tabular-nums w-5 text-right">{idx + 1}</span>
                <MapPin className={`h-3.5 w-3.5 flex-shrink-0 ${rc.active_assignment_count === 0 ? "text-warning" : "text-text-muted"}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate flex items-center gap-1.5">
                    {rc.label}
                    {rc.active_assignment_count === 0 && (
                      <Link
                        to="/dashboard/tasks"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-2xs font-medium bg-warning/20 text-warning hover:bg-warning/30 transition-colors"
                        title="此地點在此路線下尚未派發任何任務 — 點擊前往任務頁建立派發"
                      >
                        <AlertTriangle className="h-3 w-3" />
                        未派發任務
                      </Link>
                    )}
                  </div>
                  <div className="text-2xs text-text-muted truncate font-mono">{rc.code}</div>
                </div>
                <button
                  onClick={() => moveRc(idx, -1)}
                  disabled={idx === 0 || busy}
                  className="p-1 text-text-muted hover:text-text-primary disabled:opacity-30"
                  aria-label="上移"
                >
                  <ArrowUp className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => moveRc(idx, 1)}
                  disabled={idx === rcs.length - 1 || busy}
                  className="p-1 text-text-muted hover:text-text-primary disabled:opacity-30"
                  aria-label="下移"
                >
                  <ArrowDown className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => removeRc(idx)}
                  disabled={busy}
                  className="p-1 text-text-muted hover:text-danger"
                  aria-label="移除"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}

        {availableForAdd.length > 0 && (
          <div className="flex gap-2 pt-1">
            <select
              className="select flex-1"
              value={pickToAdd}
              onChange={(e) => setPickToAdd(e.target.value)}
              disabled={busy}
            >
              <option value="">— 加入地點 —</option>
              {availableForAdd.map((c) => (
                <option key={c.id} value={c.id}>{c.label}（{c.code}）</option>
              ))}
            </select>
            <button
              onClick={addRc}
              className="btn-secondary text-xs"
              disabled={!pickToAdd || busy}
            >
              <Plus className="h-3.5 w-3.5" /> 加入
            </button>
          </div>
        )}
        {availableForAdd.length === 0 && rcLoaded && siteLocations.length > 0 && (
          <p className="text-2xs text-text-muted">此案場的地點都已加入。</p>
        )}
        {siteId && siteLocations.length === 0 && (
          <p className="text-2xs text-text-muted">此案場還沒有地點，請先到「地點」頁建立。</p>
        )}
      </div>

      {err && <div className="rounded border border-danger/40 bg-danger/10 p-3 text-sm text-danger">{err}</div>}
      <div className="flex flex-row items-center justify-between gap-2">
        <div>{mode === "edit" && route && canArchive && (
          <button className="btn-danger text-sm" onClick={() => void archive()} disabled={busy}><Trash2 className="h-4 w-4" /> 封存路線</button>
        )}</div>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="btn-secondary text-sm" disabled={busy}>取消</button>
          <button data-tour-id="routes.save" onClick={() => void save()} className="btn-primary text-sm" disabled={busy || !name.trim() || !siteId}>{busy ? "儲存中…" : "儲存"}</button>
        </div>
      </div>
    </div>
  );
}

function ListSkeleton(): React.ReactElement {
  return (
    <div className="card overflow-hidden">
      <div className="p-4 border-b border-border"><Skeleton className="h-9 w-72" /></div>
      <div className="divide-y divide-border">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="flex items-center gap-4 p-4">
            <Skeleton className="h-4 w-4 rounded" />
            <Skeleton className="flex-1 h-4" />
            <Skeleton className="h-5 w-12" />
            <Skeleton className="h-5 w-12 hidden sm:block" />
          </div>
        ))}
      </div>
    </div>
  );
}
